/**
 * Profil politique cristallisé — cinématique unique entre T7 et T9.
 *
 * À ce stade le joueur a accumulé ~7 décisions ; les quatre axes du
 * `PoliticalProfile` (economic / societal / international / authority)
 * ont commencé à dessiner sa silhouette politique. Le jeu lui révèle
 * alors *« vous êtes en réalité X »* — moment-miroir clé (cf.
 * project_retention_doctrine, levier #4).
 *
 * Trigger : `worldState.turn === 8` et au moins un axe |value| > 0.10.
 * Joué une seule fois par session (mémorisé dans `localStorage`).
 *
 * Les 12 archétypes possibles sont définis par la combinaison des signes
 * dominants. La phrase qui les accompagne est ciselée — c'est le moment
 * iconique, pas un bloc de stats.
 */

import { useEffect, useState } from "react";
import type { PoliticalProfile } from "../engine/types";
import { useGame } from "../store/useGame";
import { classifyCourant, COURANT_NAME } from "../engine/courant";
import {
  classifyArchetypeKey,
  ARCHETYPE_NAME,
  type ArchetypeKey,
} from "../engine/archetypes";

const TRIGGER_TURN = 8;
const STORAGE_KEY = "mp.profileCrystallized";
/** J3 — clé de snapshot du profil à T8 (référence pour les drifts mid-term). */
export const PROFILE_T8_SNAPSHOT_KEY = "mp.profileT8Snapshot";

export interface Archetype {
  name: string;
  quote: string;
  posture: string;
}

/** R4 — table de contenu T8 (cristallisation). Indexée par ArchetypeKey. */
const T8_CONTENT: Record<ArchetypeKey, { quote: string; posture: string }> = {
  centriste_assume: {
    quote:
      "Vous tenez la barre, sans virage spectaculaire — l'Hexagone des pas-de-côté.",
    posture:
      "L'opinion vous trouve raisonnable. Les éditorialistes vous trouvent flou.",
  },
  neoconservateur: {
    quote:
      "Discipline économique, valeurs traditionnelles, frontières fermes — vous incarnez la droite que la droite n'osait plus assumer.",
    posture:
      "Le Figaro vous trouve enfin rassurant. Les jeunes ne lisent pas le Figaro.",
  },
  conservateur_liberal: {
    quote:
      "Marché ouvert, mœurs fermées, fermeté dans la rue — la synthèse historique de la Ve République.",
    posture: "Vous séduisez les héritiers et inquiétez les enfants.",
  },
  progressiste_autoritaire: {
    quote:
      "Vous voulez tout réformer, et vite — quitte à passer en force quand le débat traîne.",
    posture:
      "La gauche radicale vous applaudit ; le Conseil constitutionnel s'inquiète.",
  },
  sociale_democrate: {
    quote:
      "Redistribution, droits, ouverture européenne — la sensibilité Mitterrand-Delors revisitée.",
    posture: "Vous unifiez la gauche modérée et exaspérez sa frange radicale.",
  },
  ecologiste_pragmatique: {
    quote:
      "Transition écologique, libertés individuelles, solidarité — vous portez un Hexagone réconcilié avec son siècle.",
    posture:
      "Vous avez les ONG climat avec vous. Et personne d'autre, parfois.",
  },
  souverainiste_gauche: {
    quote:
      "Industrialisation, frontières, protection sociale — vous incarnez le Frexit de gauche qu'aucun parti n'a osé porter au bout.",
    posture:
      "Vous brouillez la carte traditionnelle. Bruxelles vous regarde d'un œil sombre.",
  },
  national_populiste: {
    quote:
      "Frontières fermes, État protecteur, la nation d'abord — l'électorat populaire vous suit, les éditorialistes s'étranglent.",
    posture:
      "Les marchés s'inquiètent, Bruxelles aussi. Votre base, elle, ne vous a jamais autant aimé.",
  },
  populiste_gouvernement: {
    quote:
      "Le peuple d'abord, l'élite ensuite — vous parlez direct, vous tranchez vite, vous ignorez les corps intermédiaires.",
    posture:
      "Les sondages vous adorent ou vous détestent — jamais l'entre-deux.",
  },
  libertaire_droite: {
    quote:
      "Marché libre, mœurs libres, État minimal — l'utopie californienne adaptée au climat tempéré.",
    posture: "Vous séduisez Silicon Sentier et inquiétez les retraités.",
  },
  liberal_classique: {
    quote:
      "Compétitivité, modernisation, ouverture — vous tenez le langage de la macronie première époque.",
    posture:
      "Vous gagnez les centres-villes ; les périphéries ne pardonnent rien.",
  },
  etatiste_reformateur: {
    quote:
      "Vous croyez à l'État puissant, capable de protéger et de transformer — gaullien dans le geste, social dans le résultat.",
    posture: "On vous compare à Pompidou. Parfois en bien.",
  },
  tenant_ordre: {
    quote:
      "Avant les politiques publiques, il y a la sécurité, le respect de l'autorité, la stabilité institutionnelle.",
    posture: "La police vous voue un culte discret. Les magistrats moins.",
  },
  souverainiste: {
    quote:
      "Vous voulez rendre à l'Hexagone les leviers qu'il a confiés. Frontières, monnaie, traités — tout doit se renégocier.",
    posture: "Bruxelles s'agace ; vos électeurs vous croient enfin sincère.",
  },
  profil_composite: {
    quote:
      "Vous tenez plusieurs lignes à la fois — c'est rare, et ça désoriente vos adversaires comme vos amis.",
    posture:
      "Personne ne sait où vous classer. C'est peut-être votre meilleure arme.",
  },
};

/**
 * R4 — Récupère le contenu T8 (quote + posture) depuis la table
 * de contenu, basé sur la classification partagée par
 * `classifyArchetypeKey`. Plus de duplication avec MandateEndOverlay.
 */
export function classifyProfile(p: PoliticalProfile): Archetype {
  const key = classifyArchetypeKey(p);
  const content = T8_CONTENT[key];
  return {
    name: ARCHETYPE_NAME[key],
    quote: content.quote,
    posture: content.posture,
  };
}

export function ProfileCrystalOverlay() {
  const worldState = useGame((s) => s.worldState);
  const [shown, setShown] = useState(false);
  const [archetype, setArchetype] = useState<Archetype | null>(null);

  useEffect(() => {
    if (!worldState) return;
    if (worldState.turn !== TRIGGER_TURN) return;
    if (localStorage.getItem(STORAGE_KEY) === "yes") return;

    const profile = worldState.polity.profile;
    const result = classifyProfile(profile);
    setArchetype(result);
    setShown(true);
  }, [worldState]);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "yes");
    // J3 — fige le profil T8 comme référence pour les overlays mid-term
    // (T20, T32, T44, T56) qui montreront la dérive depuis cet instant.
    if (worldState) {
      try {
        localStorage.setItem(
          PROFILE_T8_SNAPSHOT_KEY,
          JSON.stringify(worldState.polity.profile),
        );
      } catch {
        /* quota — ignoré */
      }
    }
    setShown(false);
  };

  if (!shown || !archetype) return null;

  return (
    <div className="profile-crystal-backdrop" onClick={close}>
      <div
        className="profile-crystal-card"
        role="dialog"
        aria-modal="true"
        aria-label="Profil politique révélé"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="crystal-pretitle">
          PROFIL CRISTALLISÉ · MOIS {worldState!.turn}
        </div>
        <div className="crystal-courant">
          {worldState
            ? COURANT_NAME[classifyCourant(worldState.polity.profile)]
            : ""}
        </div>
        <h2 className="crystal-name">{archetype.name}</h2>
        <p className="crystal-quote">« {archetype.quote} »</p>
        <p className="crystal-posture">{archetype.posture}</p>

        <button type="button" className="crystal-close" onClick={close}>
          POURSUIVRE LE MANDAT
        </button>
      </div>
    </div>
  );
}
